import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ApiService from "../../services/ApiService";
import {
  CheckCircleFill,
  XCircleFill,
  ClipboardCheck,
  TelephoneFill,
  EnvelopeFill,
  GeoAltFill,
  CalendarCheck,
  BoxSeam,
  TruckFrontFill,
  Link45deg,
  KeyFill,
  Clipboard,
  Clipboard2Check,
  SendFill,
  PersonFill,
  PinMapFill,
  ChevronDown,
} from "react-bootstrap-icons";

import dssLogo from "../../assets/images/dss-logo.png";
import { API_BASE_URL } from "../../config/config";

interface MaterialLine {
  material_description: string;
  quantity: string;
  unit: string;
}

interface OrderData {
  sales_order: {
    order_number: string;
    job_name: string;
    customer_name: string;
    delivery_address: {
      city: string;
      full: string;
    };
    delivery_date: string;
    jobsite_contact: string;
  };
  material_po: {
    vendor_name: string;
    quarry_location: string;
    // New multi-material field
    materials?: MaterialLine[];
    // Legacy single-material fields (kept for backward compat)
    material_description?: string;
    quantity?: string;
    unit?: string;
  };
  vendor: {
    pin: string;
    vendor_id: string;
  };
}

const VerifyPurchaseOrder = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [driverAccessUrl, setDriverAccessUrl] = useState<string>("");
  const [verifiedSuccessfully, setVerifiedSuccessfully] = useState(false);
  const [alreadyVerified, setAlreadyVerified] = useState(false);

  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPin, setCopiedPin] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

      // Hide hint when user is within 150px of the bottom (nothing more to scroll to)
      if (distanceFromBottom < 150) {
        setShowScrollHint(false);
      } else {
        setShowScrollHint(true);
      }
    };

    handleScroll(); // run once on mount
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const [recipients, setRecipients] = useState<string>("");
  const [customMessage, setCustomMessage] = useState<string>("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);

  useEffect(() => {
    handleVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleVerify = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await ApiService.verifyVendorPO(token, isAdmin);
      console.log("Verification response:", response);
      if (response.success || response.alreadyVerified) {
        setVerifiedSuccessfully(true);
        setAlreadyVerified(response.alreadyVerified || false);
        setDriverAccessUrl(response.driverAccessUrl || "");
        setOrderData(response.data || null);
        if (response.data?.sales_order) {
          const pin = response.data.vendor?.pin || "N/A";
          const soNumber = response.data.sales_order.order_number;
          const defaultMsg = `Decorative Stone Solutions order #${soNumber} scheduled for ${response.data.sales_order.delivery_date}. Driver PIN: ${pin}. DSS Driver Link to SO #${soNumber}: ${response.driverAccessUrl}`;
          setCustomMessage(defaultMsg);
        }
      } else {
        throw new Error(response.message || "Verification failed");
      }
    } catch (err: any) {
      setError(err.message || "Failed to accept order.");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(driverAccessUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const copyPin = () => {
    navigator.clipboard.writeText(orderData?.vendor?.pin || "N/A");
    setCopiedPin(true);
    setTimeout(() => setCopiedPin(false), 2000);
  };

  const handleSendDriverLink = async () => {
    if (!recipients.trim()) {
      setSendResult("Please enter at least one recipient");
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const recipientList = recipients.split(",").map((r) => r.trim()).filter(Boolean);
      const response = await fetch(API_BASE_URL + "/vendor/send-driver-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          recipients: recipientList,
          message: customMessage,
          link: driverAccessUrl,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSendResult(`✓ Sent to ${recipientList.length} recipient(s)`);
        setRecipients("");
        setTimeout(() => setSendResult(null), 3000);
      } else {
        setSendResult("Failed to send link. Please try again.");
      }
    } catch {
      setSendResult("Error sending link. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-light">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Verifying...</span>
        </div>
        <h5 className="text-muted">Verifying Link...</h5>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="card shadow-sm border-0 p-4 text-center" style={{ maxWidth: "500px", width: "100%" }}>
          <XCircleFill className="text-danger mb-3" size={60} />
          <h3 className="mb-3 text-danger">Verification Failed</h3>
          <p className="text-muted">{error}</p>
        </div>
      </div>
    );
  }

  if (!orderData) return null;

  const soNumber = orderData.sales_order.order_number;

  // Normalize materials: use array if provided, otherwise fall back to legacy single-material fields
  const materials: MaterialLine[] =
    orderData.material_po.materials && orderData.material_po.materials.length > 0
      ? orderData.material_po.materials
      : orderData.material_po.material_description
      ? [
          {
            material_description: orderData.material_po.material_description,
            quantity: orderData.material_po.quantity || "",
            unit: orderData.material_po.unit || "",
          },
        ]
      : [];

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", padding: "12px" }}>
      <style>{`
        /* ── Mobile-first base ── */
        .vpo-card { background: #fff; border-radius: 12px; box-shadow: 0 1px 6px rgba(0,0,0,0.08); padding: 14px 16px; margin-bottom: 12px; }
      .vpo-section-icon {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #1e3a5f;
  font-size: 17px;   /* slightly bigger */
  font-weight: 800;  /* more bold */
  margin-bottom: 10px;
}
        .vpo-section-icon svg { color: #3b82f6; }

        /* Header */
        .vpo-branding { display: flex; align-items: flex-start; gap: 12px; }
        .vpo-branding-logo { max-width: 60px; max-height: 40px; object-fit: contain; flex-shrink: 0; }
        .vpo-branding-info h2 { color: #1e3a5f; font-size: 15px; font-weight: 700; margin: 0 0 3px; }
        .vpo-branding-address { display: flex; align-items: flex-start; gap: 4px; font-size: 11px; color: #64748b; margin-bottom: 6px; line-height: 1.4; }
        .vpo-branding-address svg { flex-shrink: 0; margin-top: 1px; }
        .vpo-header-icons { display: flex; flex-direction: column; gap: 5px; }
        .vpo-header-icon-item { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #475569; min-height: 28px; }
        .vpo-header-icon-item svg { color: #1e3a5f; flex-shrink: 0; }
        .vpo-header-icon-item a { color: #1e3a5f; text-decoration: none; font-weight: 500; word-break: break-all; }
        .vpo-header-icon-item a:hover { text-decoration: underline; }

        /* Job header */
        .vpo-job-header { display: flex; flex-direction: column; gap: 10px; }
        .vpo-job-header h1 { color: #1e3a5f; font-size: 18px; font-weight: 700; margin: 0 0 3px; line-height: 1.3; }
        .vpo-job-header p { color: #64748b; font-size: 13px; margin: 0; }
        .vpo-accepted-badge { display: inline-flex; align-items: center; gap: 6px; background: #16a34a; color: #fff; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; align-self: flex-start; }

        /* Form */
        .vpo-form-box {
  background: #fffbeb; /* light yellow highlight */
  border: 2px solid #facc15; /* strong visible border */
  border-radius: 10px;
  padding: 14px;
  margin-top: 12px;
}
        .vpo-form-box h3 {
  font-size: 18px;   /* increased */
  font-weight: 800;  /* stronger */
  color: #1e3a5f;
  margin: 0 0 8px;
}
        .vpo-form-box p { font-size: 12px; color: #64748b; margin: 0 0 10px; line-height: 1.5; }
        /* 16px prevents iOS auto-zoom on focus */
        .vpo-input { width: 100%; padding: 12px; border: 1.5px solid #cbd5e1; border-radius: 8px; font-size: 16px; color: #334155; background: #fff; outline: none; box-sizing: border-box; transition: border-color 0.2s; margin-bottom: 10px; -webkit-appearance: none; }
        .vpo-input:focus { border-color: #1e3a5f; }
        .vpo-textarea { width: 100%; padding: 12px; border: 1.5px solid #cbd5e1; border-radius: 8px; font-size: 16px; color: #334155; background: #fff; outline: none; box-sizing: border-box; min-height: 120px; resize: vertical; transition: border-color 0.2s; margin-bottom: 10px; -webkit-appearance: none; }
        .vpo-textarea:focus { border-color: #1e3a5f; }
        .vpo-btn-send { width: 100%; min-height: 48px; padding: 12px 16px; background: #df7c00; color: #fff; border: none; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px; transition: background 0.2s; -webkit-tap-highlight-color: transparent; }
        .vpo-btn-send:active { background: #2d5a87; }
        .vpo-btn-send:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Divider */
        .vpo-divider { display: flex; align-items: center; gap: 10px; margin: 16px 0 8px; }
        .vpo-divider span { font-size: 12px; color: #94a3b8; white-space: nowrap; }
        .vpo-divider::before, .vpo-divider::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }

        /* Copy cards — always side by side */
        .vpo-copy-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 10px; }
        .vpo-copy-card { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 14px; display: flex; flex-direction: column; gap: 8px; }
        .vpo-copy-card-label { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
        .vpo-copy-card-label svg { color: #3b82f6; }
        .vpo-copy-card a { font-size: 14px; color: #1e3a5f; font-weight: 600; text-decoration: underline; word-break: break-all; }
        .vpo-pin-num { font-size: 32px; font-weight: 800; color: #1e3a5f; letter-spacing: 6px; margin: 0; }
        .vpo-btn-copy { min-height: 44px; padding: 10px 18px; background: #1e3a5f; color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: background 0.2s; align-self: flex-start; -webkit-tap-highlight-color: transparent; }
        .vpo-btn-copy:active { background: #2d5a87; }
        .vpo-btn-copy.copied { background: #16a34a; }

        /* Details — single column on mobile */
        .vpo-details-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
        .vpo-sub-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; }
        .vpo-sub-title { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 700; color: #1e3a5f; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #e2e8f0; }
        .vpo-sub-title svg { color: #3b82f6; }
        .vpo-info-item { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; }
        .vpo-info-item:last-child { margin-bottom: 0; }
        .vpo-info-item svg { color: #f59e0b; flex-shrink: 0; margin-top: 2px; }
        .vpo-info-label { font-size: 11px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 2px; }
        .vpo-info-value { font-size: 14px; color: #1e293b; font-weight: 500; line-height: 1.4; }

        /* Material line items */
        .vpo-material-line { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; }
        .vpo-material-line:last-child { margin-bottom: 0; }
        .vpo-material-index { font-size: 10px; font-weight: 700; color: #3b82f6; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
        .vpo-material-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
        .vpo-material-desc { font-size: 14px; color: #1e293b; font-weight: 600; line-height: 1.4; flex: 1; }
        .vpo-material-qty { font-size: 13px; color: #1e3a5f; font-weight: 700; white-space: nowrap; background: #eff6ff; padding: 4px 10px; border-radius: 6px; }

        /* Feedback */
        .vpo-send-success { background: #dcfce7; color: #166534; padding: 12px; border-radius: 7px; margin-top: 8px; font-size: 13px; font-weight: 600; text-align: center; }
        .vpo-send-error { background: #fee2e2; color: #991b1b; padding: 12px; border-radius: 7px; margin-top: 8px; font-size: 13px; font-weight: 600; text-align: center; }
        .vpo-footer { text-align: center; font-size: 12px; color: #94a3b8; padding: 16px 0 80px; }

     /* Scroll hint */
.vpo-scroll-hint { 
  position: fixed; 
  bottom: 20px;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));  /* iOS safe area */
  left: 50%; 
  transform: translateX(-50%); 
  display: flex; 
  flex-direction: row; 
  align-items: center; 
  gap: 6px; 
  background: rgba(30,58,95,0.95); 
  color: #fff; 
  padding: 10px 20px; 
  border-radius: 30px; 
  font-size: 13px; 
  font-weight: 600; 
  backdrop-filter: blur(4px); 
  cursor: pointer; 
  transition: opacity 0.4s ease; 
  z-index: 9999;                         /* ensure it's above everything */
  white-space: nowrap; 
  border: none;
  box-shadow: 0 4px 14px rgba(0,0,0,0.25);
}
.vpo-scroll-hint.hidden { 
  opacity: 0; 
  pointer-events: none; 
}
.vpo-scroll-hint:active { 
  background: rgba(30,58,95,1); 
  transform: translateX(-50%) scale(0.96);
}
.vpo-scroll-hint svg { animation: vpo-bounce 1.2s infinite; }
@keyframes vpo-bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(3px); } }

/* Mobile-specific positioning */
@media (max-width: 767px) {
  .vpo-scroll-hint {
    bottom: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    font-size: 12px;
    padding: 9px 18px;
  }
}

        /* ── Tablet / Desktop ── */
        @media (min-width: 768px) {
          div[data-vpo-page] { padding: 20px 16px; }
          .vpo-card { padding: 20px 24px; }
          .vpo-branding-logo { max-width: 90px; max-height: 55px; }
          .vpo-branding-info h2 { font-size: 18px; }
          .vpo-branding-address { font-size: 12px; }
          .vpo-header-icons { flex-direction: row; gap: 16px; }
          .vpo-job-header { flex-direction: row; justify-content: space-between; align-items: center; }
          .vpo-job-header h1 { font-size: 22px; }
          .vpo-details-grid { grid-template-columns: 1fr 1fr; gap: 16px; }
          .vpo-input, .vpo-textarea { font-size: 14px; }
          .vpo-pin-num { font-size: 28px; }
        }
      `}</style>

      <div data-vpo-page style={{ maxWidth: 900, margin: "0 auto" }}>

        {/* Company Branding */}
        <div className="vpo-card">
          <div className="vpo-branding">
            <img src={dssLogo} alt="DSS Logo" className="vpo-branding-logo" />
            <div className="vpo-branding-info" style={{ flex: 1, minWidth: 0 }}>
              <h2>Decorative Stone Solutions</h2>
              <div className="vpo-branding-address">
                <GeoAltFill size={11} color="#1e3a5f" />
                <span>197 Woodland Pkwy, STE 104 #813 • San Marcos, CA 92069</span>
              </div>
              <div className="vpo-header-icons">
                <div className="vpo-header-icon-item">
                  <TelephoneFill size={12} />
                  <a href="tel:18006991878">1-800-699-1878</a>
                </div>
                <div className="vpo-header-icon-item">
                  <EnvelopeFill size={12} />
                  <a href="mailto:billing@decorativestonesolutions.com">billing@decorativestonesolutions.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Job Header */}
        <div className="vpo-card">
          <div className="vpo-job-header">
            <div>
              <h1>{orderData.sales_order.job_name}</h1>
              <p>Sales Order #{soNumber}</p>
            </div>
            {verifiedSuccessfully && (
              <div className="vpo-accepted-badge">
                <CheckCircleFill size={15} />
                {alreadyVerified ? "Already Accepted" : "Accepted!"}
              </div>
            )}
          </div>
        </div>

        {/* Instructions for Dispatch */}
        <div className="vpo-card">
          <div className="vpo-section-icon">
            <ClipboardCheck size={18} />
            Instructions for Dispatch
          </div>
          <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 4px", lineHeight: 1.6 }}>
            Send the delivery link below to your assigned driver(s) by text or email. Enter driver(s) contact info below and hit send button to send them link from within this DSS POD APP
          </p>

          {/* Send via SMS/Email */}
          <div className="vpo-form-box">
            <h3>Send via SMS/Email</h3>
            <p>Enter driver phone number or email address. You can enter multiple contacts separated by a comma.</p>
            <input
              className="vpo-input"
              type="text"
              placeholder="Phone/email (comma separated)"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
            />
            <h3>Message driver(s) will receive</h3>
            <textarea
              className="vpo-textarea"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
            />
            <button className="vpo-btn-send" onClick={handleSendDriverLink} disabled={sending}>
              <SendFill size={13} />
              {sending ? "Sending..." : "Send"}
            </button>
            {sendResult && (
              <div className={sendResult.includes("✓") ? "vpo-send-success" : "vpo-send-error"}>
                {sendResult}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="vpo-divider">
            <span>Or use your own communication system</span>
          </div>
          <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 4px", lineHeight: 1.5 }}>
            Copy the link below and send to driver(s). Be sure to include the Driver PIN for access.
          </p>

          {/* Copy Link + Copy PIN */}
          <div className="vpo-copy-grid">
            <div className="vpo-copy-card">
              <div className="vpo-copy-card-label">
                <Link45deg size={14} />
                POD App Link
              </div>
              <a href={driverAccessUrl} target="_blank" rel="noopener noreferrer">
                DSS Driver Link to SO #{soNumber}
              </a>
              <button className={`vpo-btn-copy ${copiedLink ? "copied" : ""}`} onClick={copyLink}>
                {copiedLink ? <Clipboard2Check size={13} /> : <Clipboard size={13} />}
                {copiedLink ? "Copied!" : "Copy Link"}
              </button>
            </div>

            <div className="vpo-copy-card">
              <div className="vpo-copy-card-label">
                <KeyFill size={13} />
                Driver PIN
              </div>
              {orderData.vendor.pin ? (
                <p className="vpo-pin-num">{orderData.vendor.pin}</p>
              ) : (
                <p style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "8px 10px", margin: 0, lineHeight: 1.5 }}>
                  <strong>NO PIN SET</strong> — Set "Vendor POD PIN" in Zoho. Drivers can't login without a PIN.
                </p>
              )}
              <button className={`vpo-btn-copy ${copiedPin ? "copied" : ""}`} onClick={copyPin}>
                {copiedPin ? <Clipboard2Check size={13} /> : <Clipboard size={13} />}
                {copiedPin ? "Copied!" : "Copy PIN"}
              </button>
            </div>
          </div>
        </div>

        {/* Order Details */}
        <div className="vpo-card">
          <div className="vpo-section-icon">
            <BoxSeam size={17} />
            Order Details
          </div>
          <div className="vpo-details-grid">
            {/* Material Pickup */}
      {/* Material Pickup */}
<div className="vpo-sub-card">
  <div className="vpo-sub-title">
    <TruckFrontFill size={15} />
    Material Pickup
  </div>
  <div className="vpo-info-item">
    <TruckFrontFill size={15} />
    <div>
      <div className="vpo-info-label">Vendor</div>
      <div className="vpo-info-value">{orderData.material_po.vendor_name}</div>
    </div>
  </div>
  <div className="vpo-info-item">
    <GeoAltFill size={15} />
    <div>
      <div className="vpo-info-label">Location</div>
      <div className="vpo-info-value">{orderData.material_po.quarry_location || "N/A"}</div>
    </div>
  </div>

  {/* Materials + Quantities — old style, repeated per line item */}
  {/* Materials — numbered list under one label */}
{materials.length === 0 ? (
  <>
    <div className="vpo-info-item">
      <BoxSeam size={15} />
      <div>
        <div className="vpo-info-label">Material</div>
        <div className="vpo-info-value">N/A</div>
      </div>
    </div>
    <div className="vpo-info-item">
      <BoxSeam size={15} />
      <div>
        <div className="vpo-info-label">Quantity</div>
        <div className="vpo-info-value">N/A</div>
      </div>
    </div>
  </>
) : (
<>
    <div className="vpo-info-item">
      <BoxSeam size={15} />
      <div style={{ flex: 1 }}>
        <div className="vpo-info-label">
          {materials.length > 1 ? `Materials (${materials.length})` : "Material"}
        </div>
        {materials.length === 1 ? (
          <div className="vpo-info-value">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <span>{materials[0].material_description}</span>
              <span style={{
                fontSize: 13,
                color: "#1e3a5f",
                fontWeight: 700,
                whiteSpace: "nowrap",
                background: "#eff6ff",
                padding: "2px 10px",
                borderRadius: 6,
              }}>
                {parseFloat(materials[0].quantity || "0").toFixed(2)} {materials[0].unit}
              </span>
            </div>
          </div>
        ) : (
          <div className="vpo-info-value">
            {materials.map((m, idx) => (
              <div key={idx} style={{
                marginBottom: idx < materials.length - 1 ? 6 : 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 12,
              }}>
                <span style={{ flex: 1 }}>
                  <span style={{ color: "#64748b", fontWeight: 600, marginRight: 4 }}>{idx + 1}.</span>
                  <span>{m.material_description}</span>
                </span>
                <span style={{
                  fontSize: 13,
                  color: "#1e3a5f",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  background: "#eff6ff",
                  padding: "2px 10px",
                  borderRadius: 6,
                }}>
                  {parseFloat(m.quantity || "0").toFixed(2)} {m.unit}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    <div className="vpo-info-item">
      <BoxSeam size={15} />
      <div>
        <div className="vpo-info-label">Total Quantity</div>
        <div className="vpo-info-value">
          {materials
            .reduce((sum, m) => sum + (parseFloat(m.quantity) || 0), 0)
            .toFixed(2)}{" "}
          {materials[0].unit}
        </div>
      </div>
    </div>
  </>
)}
</div>

            {/* Delivery Location */}
            <div className="vpo-sub-card">
              <div className="vpo-sub-title">
                <PinMapFill size={15} />
                Delivery Location
              </div>
              <div className="vpo-info-item">
                <PersonFill size={15} />
                <div>
                  <div className="vpo-info-label">Customer</div>
                  <div className="vpo-info-value">{orderData.sales_order.customer_name}</div>
                </div>
              </div>
              <div className="vpo-info-item">
                <GeoAltFill size={15} />
                <div>
                  <div className="vpo-info-label">Address</div>
                  <div className="vpo-info-value">{orderData.sales_order.delivery_address.full}</div>
                </div>
              </div>
              <div className="vpo-info-item">
                <CalendarCheck size={15} />
                <div>
                  <div className="vpo-info-label">Delivery Date</div>
                  <div className="vpo-info-value">{orderData.sales_order.delivery_date}</div>
                </div>
              </div>
              {orderData.sales_order.jobsite_contact !== "N/A" && (
                <div className="vpo-info-item">
                  <TelephoneFill size={15} />
                  <div>
                    <div className="vpo-info-label">Contact</div>
                    <div className="vpo-info-value">{orderData.sales_order.jobsite_contact}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="vpo-footer">© 2026 Decorative Stone Solutions • DSS POD App</div>
      </div>

      {/* Scroll hint */}
      <button
        type="button"
        className={`vpo-scroll-hint ${showScrollHint ? "" : "hidden"}`}
        onClick={() => {
          window.scrollBy({
            top: window.innerHeight,          // full viewport height — much more scroll
            behavior: "smooth"
          });
          // Don't hide immediately so user can tap again to keep scrolling
          // Hide only after they reach near the bottom (handled by scroll listener)
        }}
      >
        Scroll down for more
        <ChevronDown size={14} />
      </button>
    </div>
  );
};

export default VerifyPurchaseOrder;